const INTERACTIVE_EVENT_NAMES = new Set([
  'click',
  'dblclick',
  'mousedown',
  'mouseup',
  'mouseover',
  'mouseout',
  'mousemove',
  'contextmenu'
])

let colorProbeContext = null

const clampColorChannel = function (value) {
  const num = Number(value)
  if (!Number.isFinite(num)) return 0
  return Math.max(0, Math.min(255, Math.round(num)))
}

const toHexChannel = function (value) {
  return clampColorChannel(value).toString(16).padStart(2, '0')
}

const rgbToHex = function (rgb) {
  if (!rgb) return ''
  return '#' + toHexChannel(rgb.r) + toHexChannel(rgb.g) + toHexChannel(rgb.b)
}

const formatDashNumber = function (value) {
  const num = Number(value)
  if (!Number.isFinite(num)) return '0'
  const rounded = Math.round(num * 10) / 10
  if (Math.abs(rounded - Math.round(rounded)) < 1e-9) {
    return String(Math.round(rounded))
  }
  return rounded.toFixed(1).replace(/\.0$/, '')
}

const normalizeDashArrayTokens = function (dashArray) {
  if (Array.isArray(dashArray)) {
    return dashArray
      .map(function (value) { return Number(value) })
      .filter(function (value) { return Number.isFinite(value) && value > 0 })
  }
  return String(dashArray || '')
    .split(/[,\s]+/)
    .map(function (value) { return Number(value) })
    .filter(function (value) { return Number.isFinite(value) && value > 0 })
}

const resolveSecondaryDashOffset = function (dashArray, dashOffset) {
  const tokens = normalizeDashArrayTokens(dashArray)
  if (!tokens.length) return Number(dashOffset) || 0
  return (Number(dashOffset) || 0) + tokens[0]
}

const resolveBalancedDashArray = function (dashArray) {
  const tokens = normalizeDashArrayTokens(dashArray)
  if (!tokens.length) return ''
  const first = tokens[0]
  const second = tokens.length > 1 ? tokens[1] : first
  const balanced = Math.max(1, (first + second) / 2)
  return formatDashNumber(balanced) + ',' + formatDashNumber(balanced)
}

const getColorProbeContext = function () {
  if (colorProbeContext) return colorProbeContext
  if (typeof document === 'undefined' || typeof document.createElement !== 'function') return null
  const canvas = document.createElement('canvas')
  if (!canvas || typeof canvas.getContext !== 'function') return null
  colorProbeContext = canvas.getContext('2d')
  return colorProbeContext
}

const parseHexColor = function (text) {
  const clean = String(text || '').trim().replace(/^#/, '')
  if (!/^[0-9a-fA-F]+$/.test(clean)) return null
  if (clean.length === 3) {
    return {
      r: parseInt(clean[0] + clean[0], 16),
      g: parseInt(clean[1] + clean[1], 16),
      b: parseInt(clean[2] + clean[2], 16)
    }
  }
  if (clean.length >= 6) {
    return {
      r: parseInt(clean.slice(0, 2), 16),
      g: parseInt(clean.slice(2, 4), 16),
      b: parseInt(clean.slice(4, 6), 16)
    }
  }
  return null
}

const parseRgbColor = function (text) {
  const match = String(text || '').trim().match(/^rgba?\(([^)]+)\)$/i)
  if (!match) return null
  const parts = match[1].split(',').map(function (part) { return Number(part.trim()) })
  if (parts.length < 3) return null
  return {
    r: clampColorChannel(parts[0]),
    g: clampColorChannel(parts[1]),
    b: clampColorChannel(parts[2])
  }
}

const parseCssColor = function (rawColor) {
  const text = String(rawColor || '').trim()
  if (!text) return null
  const directHex = parseHexColor(text)
  if (directHex) return directHex
  const directRgb = parseRgbColor(text)
  if (directRgb) return directRgb
  const ctx = getColorProbeContext()
  if (!ctx) return null
  const previous = ctx.fillStyle
  try {
    ctx.fillStyle = '#000000'
    ctx.fillStyle = text
    const normalized = String(ctx.fillStyle || '')
    ctx.fillStyle = previous
    return parseHexColor(normalized) || parseRgbColor(normalized)
  } catch (_error) {
    try {
      ctx.fillStyle = previous
    } catch (_restoreError) {}
    return null
  }
}

const resolveRelativeLuminance = function (rgb) {
  if (!rgb) return 0
  const toLinear = function (value) {
    const channel = clampColorChannel(value) / 255
    if (channel <= 0.03928) return channel / 12.92
    return Math.pow((channel + 0.055) / 1.055, 2.4)
  }
  return (0.2126 * toLinear(rgb.r)) + (0.7152 * toLinear(rgb.g)) + (0.0722 * toLinear(rgb.b))
}

const resolveColorSpread = function (rgb) {
  if (!rgb) return 0
  const values = [rgb.r, rgb.g, rgb.b].map(clampColorChannel)
  return Math.max.apply(null, values) - Math.min.apply(null, values)
}

const rgbToHsl = function (rgb) {
  if (!rgb) return null
  const r = clampColorChannel(rgb.r) / 255
  const g = clampColorChannel(rgb.g) / 255
  const b = clampColorChannel(rgb.b) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const lightness = (max + min) / 2
  if (Math.abs(max - min) < 1e-9) {
    return { h: 0, s: 0, l: lightness }
  }
  const delta = max - min
  const saturation = lightness > 0.5 ? (delta / (2 - max - min)) : (delta / (max + min))
  let hue = 0
  if (max === r) {
    hue = ((g - b) / delta) + (g < b ? 6 : 0)
  } else if (max === g) {
    hue = ((b - r) / delta) + 2
  } else {
    hue = ((r - g) / delta) + 4
  }
  hue /= 6
  return { h: hue, s: saturation, l: lightness }
}

const hslChannelToRgb = function (p, q, t) {
  let value = t
  if (value < 0) value += 1
  if (value > 1) value -= 1
  if (value < (1 / 6)) return p + ((q - p) * 6 * value)
  if (value < (1 / 2)) return q
  if (value < (2 / 3)) return p + ((q - p) * ((2 / 3) - value) * 6)
  return p
}

const hslToRgb = function (hsl) {
  if (!hsl) return null
  const hue = ((Number(hsl.h) % 1) + 1) % 1
  const saturation = Math.max(0, Math.min(1, Number(hsl.s)))
  const lightness = Math.max(0, Math.min(1, Number(hsl.l)))
  if (saturation <= 0) {
    const gray = clampColorChannel(lightness * 255)
    return { r: gray, g: gray, b: gray }
  }
  const q = lightness < 0.5
    ? lightness * (1 + saturation)
    : lightness + saturation - (lightness * saturation)
  const p = (2 * lightness) - q
  return {
    r: clampColorChannel(hslChannelToRgb(p, q, hue + (1 / 3)) * 255),
    g: clampColorChannel(hslChannelToRgb(p, q, hue) * 255),
    b: clampColorChannel(hslChannelToRgb(p, q, hue - (1 / 3)) * 255)
  }
}

const isNearBlackOrWhite = function (rgb) {
  if (!rgb) return false
  const luminance = resolveRelativeLuminance(rgb)
  const spread = resolveColorSpread(rgb)
  return spread < 18 && (luminance < 0.08 || luminance > 0.9)
}

const resolveDualDashPalette = function (primaryColor, fallbackColor) {
  const primaryRgb = parseCssColor(primaryColor)
  if (!primaryRgb) {
    return {
      primaryColor: String(primaryColor || fallbackColor || '#ffffff'),
      secondaryColor: String(fallbackColor || '#ffffff')
    }
  }
  const spread = resolveColorSpread(primaryRgb)
  if (isNearBlackOrWhite(primaryRgb)) {
    return {
      primaryColor: resolveRelativeLuminance(primaryRgb) > 0.72 ? '#ffffff' : '#000000',
      secondaryColor: resolveRelativeLuminance(primaryRgb) > 0.72 ? '#000000' : '#ffffff'
    }
  }
  if (spread < 18) {
    return {
      primaryColor: '#ffea00',
      secondaryColor: '#0047ff'
    }
  }
  const hsl = rgbToHsl(primaryRgb)
  if (!hsl) {
    return {
      primaryColor: String(primaryColor || fallbackColor || '#ffffff'),
      secondaryColor: String(fallbackColor || '#ffffff')
    }
  }
  return {
    primaryColor: rgbToHex(hslToRgb({
      h: hsl.h,
      s: 0.95,
      l: 0.52
    })),
    secondaryColor: rgbToHex(hslToRgb({
      h: hsl.h + 0.5,
      s: 0.98,
      l: 0.56
    }))
  }
}

const resolveDualDashSecondaryColor = function (primaryColor, fallbackColor) {
  return resolveDualDashPalette(primaryColor, fallbackColor).secondaryColor
}

const buildPathClone = function (layer, L, styleOptions) {
  if (!layer || !L) return null
  if (L.Circle && layer instanceof L.Circle) {
    return L.circle(layer.getLatLng(), Object.assign({}, styleOptions, {
      radius: (typeof layer.getRadius === 'function') ? layer.getRadius() : Number(styleOptions.radius) || 0
    }))
  }
  if (L.Polygon && layer instanceof L.Polygon) {
    return L.polygon(layer.getLatLngs(), styleOptions)
  }
  return L.polyline(layer.getLatLngs(), styleOptions)
}

const syncCloneGeometry = function (sourceLayer, targetLayer) {
  if (!sourceLayer || !targetLayer) return
  if (typeof sourceLayer.getRadius === 'function' && typeof targetLayer.setRadius === 'function') {
    if (typeof sourceLayer.getLatLng === 'function' && typeof targetLayer.setLatLng === 'function') {
      targetLayer.setLatLng(sourceLayer.getLatLng())
    }
    targetLayer.setRadius(sourceLayer.getRadius())
    return
  }
  if (typeof sourceLayer.getLatLngs === 'function' && typeof targetLayer.setLatLngs === 'function') {
    targetLayer.setLatLngs(sourceLayer.getLatLngs())
  }
}

const applyPathInteractivity = function (layer, interactive) {
  if (!layer || !layer._path) return
  layer._path.style.pointerEvents = interactive ? '' : 'none'
  if (interactive) {
    layer._path.classList.add('leaflet-interactive')
  } else {
    layer._path.classList.remove('leaflet-interactive')
  }
}

const buildSecondaryStyle = function (layer, config) {
  const baseOptions = Object.assign({}, layer && layer.options ? layer.options : {})
  return Object.assign(baseOptions, {
    color: config.secondaryColor || resolveDualDashSecondaryColor(baseOptions.color, '#ffffff'),
    dashArray: resolveBalancedDashArray(baseOptions.dashArray),
    dashOffset: resolveSecondaryDashOffset(resolveBalancedDashArray(baseOptions.dashArray), baseOptions.dashOffset),
    fill: false,
    fillOpacity: 0,
    interactive: false,
    bubblingMouseEvents: false,
    className: ((baseOptions.className || '') + ' portal-dual-dash-secondary').trim()
  })
}

const buildHitStyle = function (layer) {
  const baseOptions = Object.assign({}, layer && layer.options ? layer.options : {})
  return Object.assign(baseOptions, {
    color: '#000000',
    opacity: 0,
    fillOpacity: 0,
    dashArray: null,
    dashOffset: 0,
    weight: Math.max(Number(baseOptions.weight) || 0, 12),
    interactive: true,
    bubblingMouseEvents: true,
    className: ((baseOptions.className || '') + ' portal-dual-dash-hit').trim()
  })
}

const decorateLeafletPathDualDash = function (layer, {
  L = (typeof window !== 'undefined' ? window.L : null),
  secondaryColor = '',
  useHitLayer = false
} = {}) {
  if (!layer || !L || !layer.options || !layer.options.dashArray) return layer
  if (layer._portalDualDashState) return layer

  const initialPalette = resolveDualDashPalette(layer.options.color, '#ffffff')
  layer.options.color = initialPalette.primaryColor
  layer.options.dashArray = resolveBalancedDashArray(layer.options.dashArray) || layer.options.dashArray
  if (typeof layer.setStyle === 'function' && layer._map) {
    layer.setStyle({
      color: layer.options.color,
      dashArray: layer.options.dashArray
    })
  }

  const state = {
    L: L,
    secondaryColor: secondaryColor || initialPalette.secondaryColor,
    useHitLayer: !!useHitLayer
  }

  state.companionLayer = buildPathClone(layer, L, buildSecondaryStyle(layer, state))
  state.hitLayer = state.useHitLayer ? buildPathClone(layer, L, buildHitStyle(layer)) : null
  layer._portalDualDashState = state

  const syncStyles = function () {
    if (state.companionLayer && typeof state.companionLayer.setStyle === 'function') {
      state.companionLayer.setStyle(buildSecondaryStyle(layer, state))
    }
    if (state.hitLayer && typeof state.hitLayer.setStyle === 'function') {
      state.hitLayer.setStyle(buildHitStyle(layer))
    }
  }

  const syncGeometry = function () {
    syncCloneGeometry(layer, state.companionLayer)
    syncCloneGeometry(layer, state.hitLayer)
  }

  const syncLinkedLayers = function () {
    syncStyles()
    syncGeometry()
  }

  const attachLinkedLayers = function () {
    const map = layer._map
    if (!map) return
    syncLinkedLayers()
    if (state.companionLayer && !map.hasLayer(state.companionLayer)) {
      state.companionLayer.addTo(map)
    }
    if (state.hitLayer && !map.hasLayer(state.hitLayer)) {
      state.hitLayer.addTo(map)
      if (typeof state.hitLayer.bringToFront === 'function') {
        state.hitLayer.bringToFront()
      }
    }
    if (state.useHitLayer) {
      applyPathInteractivity(layer, false)
    }
    applyPathInteractivity(state.companionLayer, false)
  }

  const detachLinkedLayers = function () {
    const companionMap = state.companionLayer && state.companionLayer._map ? state.companionLayer._map : null
    if (companionMap && companionMap.hasLayer(state.companionLayer)) {
      companionMap.removeLayer(state.companionLayer)
    }
    const hitMap = state.hitLayer && state.hitLayer._map ? state.hitLayer._map : null
    if (hitMap && hitMap.hasLayer(state.hitLayer)) {
      hitMap.removeLayer(state.hitLayer)
    }
  }

  layer.on('add', attachLinkedLayers)
  layer.on('remove', detachLinkedLayers)

  if (typeof layer.setStyle === 'function') {
    const originalSetStyle = layer.setStyle.bind(layer)
    layer.setStyle = function (styleOptions) {
      const nextStyle = Object.assign({}, styleOptions || {})
      if (Object.prototype.hasOwnProperty.call(nextStyle, 'dashArray')) {
        nextStyle.dashArray = resolveBalancedDashArray(nextStyle.dashArray) || nextStyle.dashArray
      }
      if (Object.prototype.hasOwnProperty.call(nextStyle, 'color')) {
        const nextPalette = resolveDualDashPalette(nextStyle.color, state.secondaryColor)
        nextStyle.color = nextPalette.primaryColor
        state.secondaryColor = nextPalette.secondaryColor
      }
      originalSetStyle(nextStyle)
      syncStyles()
      attachLinkedLayers()
      return layer
    }
  }

  if (typeof layer.setLatLngs === 'function') {
    const originalSetLatLngs = layer.setLatLngs.bind(layer)
    layer.setLatLngs = function (latlngs) {
      originalSetLatLngs(latlngs)
      syncGeometry()
      return layer
    }
  }

  if (typeof layer.setLatLng === 'function') {
    const originalSetLatLng = layer.setLatLng.bind(layer)
    layer.setLatLng = function (latlng) {
      originalSetLatLng(latlng)
      syncGeometry()
      return layer
    }
  }

  if (typeof layer.setRadius === 'function') {
    const originalSetRadius = layer.setRadius.bind(layer)
    layer.setRadius = function (radius) {
      originalSetRadius(radius)
      syncGeometry()
      return layer
    }
  }

  if (state.hitLayer) {
    if (typeof layer.bindTooltip === 'function') {
      const originalBindTooltip = layer.bindTooltip.bind(layer)
      layer.bindTooltip = function (content, options) {
        originalBindTooltip(content, options)
        if (typeof state.hitLayer.bindTooltip === 'function') {
          state.hitLayer.bindTooltip(content, options)
        }
        return layer
      }
    }
    if (typeof layer.unbindTooltip === 'function') {
      const originalUnbindTooltip = layer.unbindTooltip.bind(layer)
      layer.unbindTooltip = function () {
        originalUnbindTooltip()
        if (typeof state.hitLayer.unbindTooltip === 'function') {
          state.hitLayer.unbindTooltip()
        }
        return layer
      }
    }
    if (typeof layer.on === 'function') {
      const originalOn = layer.on.bind(layer)
      layer.on = function (types, fn, context) {
        originalOn(types, fn, context)
        const eventNames = String(types || '').split(/\s+/).filter(Boolean)
        if (eventNames.some(function (eventName) { return INTERACTIVE_EVENT_NAMES.has(eventName) })) {
          state.hitLayer.on(types, fn, context)
        }
        return layer
      }
    }
    if (typeof layer.off === 'function') {
      const originalOff = layer.off.bind(layer)
      layer.off = function (types, fn, context) {
        originalOff(types, fn, context)
        const eventNames = String(types || '').split(/\s+/).filter(Boolean)
        if (eventNames.some(function (eventName) { return INTERACTIVE_EVENT_NAMES.has(eventName) })) {
          state.hitLayer.off(types, fn, context)
        }
        return layer
      }
    }
  }

  if (layer._map) {
    attachLinkedLayers()
  }

  return layer
}

const installLeafletDrawDualDashPreview = function ({
  L = (typeof window !== 'undefined' ? window.L : null),
  map = null,
  drawLayer = null,
  secondaryColor = ''
} = {}) {
  if (!L || !map || !drawLayer) {
    return function () {}
  }

  let previewLayer = null
  let freeSegmentPrimary = null
  let freeSegmentSecondary = null

  const removePreview = function () {
    if (!previewLayer || !previewLayer._map) return
    previewLayer._map.removeLayer(previewLayer)
    previewLayer = null
  }

  const removeFreeSegment = function () {
    if (freeSegmentPrimary && freeSegmentPrimary._map) {
      freeSegmentPrimary._map.removeLayer(freeSegmentPrimary)
    }
    if (freeSegmentSecondary && freeSegmentSecondary._map) {
      freeSegmentSecondary._map.removeLayer(freeSegmentSecondary)
    }
    freeSegmentPrimary = null
    freeSegmentSecondary = null
  }

  const syncFreeSegment = function () {
    const sourceLayer = drawLayer._poly
    const markers = Array.isArray(drawLayer._markers) ? drawLayer._markers : []
    const mouseMarker = drawLayer._mouseMarker
    const lastMarker = markers.length ? markers[markers.length - 1] : null
    const lastLatLng = lastMarker && typeof lastMarker.getLatLng === 'function' ? lastMarker.getLatLng() : null
    const mouseLatLng = mouseMarker && typeof mouseMarker.getLatLng === 'function' ? mouseMarker.getLatLng() : null
    if (!sourceLayer || !sourceLayer.options || !sourceLayer.options.dashArray || !lastLatLng || !mouseLatLng) {
      removeFreeSegment()
      return
    }
    const palette = resolveDualDashPalette(sourceLayer.options.color, '#ffffff')
    const balancedDashArray = resolveBalancedDashArray(sourceLayer.options.dashArray) || sourceLayer.options.dashArray
    const primaryStyle = Object.assign({}, sourceLayer.options, {
      color: palette.primaryColor,
      dashArray: balancedDashArray,
      interactive: false,
      bubblingMouseEvents: false,
      fill: false,
      fillOpacity: 0
    })
    const secondaryStyle = Object.assign({}, primaryStyle, {
      color: secondaryColor || palette.secondaryColor,
      dashOffset: resolveSecondaryDashOffset(balancedDashArray, sourceLayer.options.dashOffset)
    })
    const latlngs = [lastLatLng, mouseLatLng]
    if (!freeSegmentPrimary) {
      freeSegmentPrimary = L.polyline(latlngs, primaryStyle).addTo(map)
      freeSegmentSecondary = L.polyline(latlngs, secondaryStyle).addTo(map)
    } else {
      freeSegmentPrimary.setStyle(primaryStyle)
      freeSegmentPrimary.setLatLngs(latlngs)
      freeSegmentSecondary.setStyle(secondaryStyle)
      freeSegmentSecondary.setLatLngs(latlngs)
    }
    applyPathInteractivity(freeSegmentPrimary, false)
    applyPathInteractivity(freeSegmentSecondary, false)
    if (typeof freeSegmentPrimary.bringToFront === 'function') {
      freeSegmentPrimary.bringToFront()
    }
    if (typeof freeSegmentSecondary.bringToFront === 'function') {
      freeSegmentSecondary.bringToFront()
    }
  }

  const syncPreview = function () {
    const sourceLayer = drawLayer._poly
    if (!sourceLayer || !sourceLayer.options || !sourceLayer.options.dashArray) {
      removePreview()
      removeFreeSegment()
      return
    }
    const palette = resolveDualDashPalette(sourceLayer.options.color, '#ffffff')
    sourceLayer.options.color = palette.primaryColor
    sourceLayer.options.dashArray = resolveBalancedDashArray(sourceLayer.options.dashArray) || sourceLayer.options.dashArray
    if (typeof sourceLayer.setStyle === 'function') {
      sourceLayer.setStyle({
        color: sourceLayer.options.color,
        dashArray: sourceLayer.options.dashArray
      })
    }
    const previewStyle = buildSecondaryStyle(sourceLayer, {
      secondaryColor: secondaryColor || palette.secondaryColor
    })
    if (!previewLayer) {
      previewLayer = buildPathClone(sourceLayer, L, previewStyle)
      if (!previewLayer) return
      previewLayer.addTo(map)
      if (typeof previewLayer.bringToFront === 'function') {
        previewLayer.bringToFront()
      }
    } else {
      syncCloneGeometry(sourceLayer, previewLayer)
      if (typeof previewLayer.setStyle === 'function') {
        previewLayer.setStyle(previewStyle)
      }
    }
    applyPathInteractivity(previewLayer, false)
    syncFreeSegment()
  }

  const cleanup = function () {
    map.off('mousemove', syncPreview)
    map.off('draw:drawvertex', syncPreview)
    removePreview()
    removeFreeSegment()
  }

  map.on('mousemove', syncPreview)
  map.on('draw:drawvertex', syncPreview)
  map.once('draw:drawstop', cleanup)
  syncPreview()

  return cleanup
}

export {
  resolveBalancedDashArray,
  resolveDualDashSecondaryColor,
  decorateLeafletPathDualDash,
  installLeafletDrawDualDashPreview
}
